import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Shield,
  AlertTriangle,
  RefreshCw,
  UserCheck,
  X,
  Save,
  CheckCircle2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MaintenanceModePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [exemptEmails, setExemptEmails] = useState([]);
  const [message, setMessage] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  // Load settings + users
  useEffect(() => {
    const load = async () => {
      // Load settings and users independently so one failure doesn't block the other
      try {
        const settingsRes = await axios.get(`${API_URL}/api/maintenance/settings`, { headers: getAuthHeader() });
        setEnabled(settingsRes.data.enabled);
        setExemptEmails(settingsRes.data.exempt_emails || []);
        setMessage(settingsRes.data.message || '');
      } catch (error) {
        console.error('Maintenance settings error:', error.response?.status, error.response?.data);
        toast.error('Failed to load maintenance settings: ' + (error.response?.data?.detail || error.message));
      }
      try {
        const usersRes = await axios.get(`${API_URL}/api/maintenance/users`, { headers: getAuthHeader() });
        setAllUsers(usersRes.data || []);
      } catch (error) {
        console.error('Users list error:', error.response?.status, error.response?.data);
        toast.error('Failed to load users list: ' + (error.response?.data?.detail || error.message));
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/maintenance/settings`, {
        enabled,
        exempt_emails: exemptEmails,
        message,
      }, { headers: getAuthHeader() });
      setEnabled(res.data.enabled);
      setExemptEmails(res.data.exempt_emails || []);
      toast.success(enabled ? 'Maintenance mode ENABLED' : 'Maintenance mode DISABLED');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addExemptUser = (email) => {
    const normalized = email.trim().toLowerCase();
    if (normalized && !exemptEmails.includes(normalized)) {
      setExemptEmails([...exemptEmails, normalized]);
    }
    setSearchTerm('');
  };

  const removeExemptUser = (email) => {
    setExemptEmails(exemptEmails.filter(e => e !== email));
  };

  // Filter users for dropdown
  const filteredUsers = allUsers.filter(u => {
    if (!searchTerm.trim()) return false;
    const email = (u.email || '').toLowerCase();
    const name = (u.name || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return (email.includes(term) || name.includes(term)) && !exemptEmails.includes(email);
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading settings...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-4 py-4" data-testid="maintenance-mode-page">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5" />
              Maintenance Mode
            </CardTitle>
            <CardDescription>
              Block all users from accessing the system. Exempt users can still log in and work normally.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Toggle */}
            <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${enabled ? 'border-red-300 bg-red-50' : 'border-green-200 bg-green-50'}`}>
              <div className="flex items-center gap-3">
                {enabled ? (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                )}
                <div>
                  <p className="font-semibold text-sm" data-testid="maintenance-status-label">
                    {enabled ? 'Maintenance Mode is ON' : 'System is Live'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {enabled ? 'Only exempt users can access the system' : 'All users can access the system'}
                  </p>
                </div>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="maintenance-toggle"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="maint-message" className="text-sm font-medium">Message shown to blocked users</Label>
              <Input
                id="maint-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g. FY closing in progress..."
                data-testid="maintenance-message-input"
              />
            </div>

            {/* Exempt Users */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                Exempt Users ({exemptEmails.length})
              </Label>

              {/* Current exempt list */}
              {exemptEmails.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {exemptEmails.map(email => {
                    const user = allUsers.find(u => u.email?.toLowerCase() === email);
                    return (
                      <Badge
                        key={email}
                        variant="secondary"
                        className="flex items-center gap-1 py-1 px-2"
                      >
                        <span className="text-xs">{user?.name || email}</span>
                        <button
                          onClick={() => removeExemptUser(email)}
                          className="ml-1 hover:text-red-600 transition-colors"
                          data-testid={`remove-exempt-${email}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No exempt users — everyone will be blocked when maintenance is on.</p>
              )}

              {/* Search & add */}
              <div className="relative">
                <Input
                  placeholder="Search user by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="exempt-user-search"
                />
                {filteredUsers.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredUsers.map(u => (
                      <button
                        key={u.email}
                        className="w-full text-left px-3 py-2 hover:bg-slate-100 transition-colors flex justify-between items-center text-sm"
                        onClick={() => addExemptUser(u.email)}
                        data-testid={`add-exempt-${u.email}`}
                      >
                        <span>
                          <span className="font-medium">{u.name}</span>
                          <span className="text-muted-foreground ml-2 text-xs">{u.email}</span>
                        </span>
                        <UserCheck className="w-3.5 h-3.5 text-green-600" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full"
              variant={enabled ? 'destructive' : 'default'}
              data-testid="save-maintenance-settings"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Saving...' : enabled ? 'Save & Enable Maintenance Mode' : 'Save Settings'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
